import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "pb-x3" }
const _hoisted_2 = { class: "flex flex-col-reverse gap-32 md:flex-row-reverse md:gap-80" }
const _hoisted_3 = { class: "flex-1 md:w-0" }
const _hoisted_4 = { class: "flex-1 md:w-0" }

import { computed, defineAsyncComponent, onUnmounted, watch } from 'vue';
import ProductDetailIntro from '@/shared/product/details/ui/ProductDetailIntro.vue';
import ProductDetailVisualization from '@/shared/product/details/ui/visualization/ProductDetailVisualization.vue';
import ProductDetailPrice from '@/shared/product/details/ui/ProductDetailPrice.vue';
import CustomizerMini from '@/shared/product/customizer/ui/CustomizerMini.vue';
import CustomizerOverlays from '@/shared/product/customizer/ui/overlays/CustomizerOverlays.vue';
import Breakpoints from '@/core/responsive/breakpoints/Breakpoints.vue';
import useProductDetailData from '@/shared/product/details/useProductDetailData';
import ProductDetailNetPrice from '@/b2b/product/ui/ProductDetailNetPrice.vue';
import ProductDetailDelivery from '@/shared/product/details/ui/ProductDetailDelivery.vue';
import ProductDetailItemId from '@/b2b/product/ui/ProductDetailItemId.vue';
import ProductDetailUpsellOverlay from '@/shared/product/upsell/ui/ProductDetailUpsellOverlay.vue';
import authStore from '@/shared/auth/auth.store';
import ProductDetailClimateEmissions from '@/shared/product/details/ui/ProductDetailClimateEmissions.vue';
import ProductDetailCertification from '@/shared/product/details/ui/ProductDetailCertification.vue';
import { useProductImages } from '@/shared/product/details/useProductImages';
import useProductDetailJsonLd from '@/shared/product/details/useProductDetailJsonLd';
import ProductAddOns from '@/shared/product/add-ons/ProductAddOns.vue';
import useProductAddOns from '@/shared/product/add-ons/useProductAddOns';
import { useModuleConfigurator } from '@/shared/product/details/ui/module-configurator/useModuleConfigurator';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import { QueryKeys } from '@/shared/config/route-query.config';
import { useRouteQuery } from '@/core/browser/route-query.composable';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductDetail',
  setup(__props) {

const ProductConfigurableModulesOverlay = defineAsyncComponent(() => import(/* webpackChunkName: 'MultiConfiguration', webpackPrefetch: true */ '@/shared/product/details/ui/ProductConfigurableModulesOverlay.vue'));
const ModuleConfigurator = defineAsyncComponent(() => import(/* webpackChunkName: 'ModuleConfigurator', webpackPrefetch: true */ '@/shared/product/details/ui/module-configurator/ModuleConfigurator.vue'));

const {
    productData,
    variantId,
    coverMaterial,
    lightingSpecification,
    productCertificationPrimary,
    productAddOnCount,
    environmentalImages,
    productName,
} = useProductDetailData();

const { clearAddOns } = useProductAddOns();
const { getProductImages } = useProductImages();
const { jsonLd } = useProductDetailJsonLd();
const labels = useBlockLabels();

const configurationId = useRouteQuery(QueryKeys.PDPConfigurationId);
const { isConfiguratorToggled, closeConfigurator, is3DConfiguratorFeatureEnabled, configurationImages, setConfigurationLabels, resetModuleConfiguration } = useModuleConfigurator();

if (is3DConfiguratorFeatureEnabled.value) {
    watch(configurationId, (newId) => {
        setConfigurationLabels({
            name: productName,
            moduleCountTitle: labels.moduleCountTitle,
            moduleCountTitlePlural: labels.moduleCountTitlePlural,
        });

        if (!newId) {
            resetModuleConfiguration();
        }
    }, { immediate: true });
}

onUnmounted(() => clearAddOns);

const productImages = computed(() => getProductImages(variantId.value, productData.value.imageProperties));

const isSignedInAsPartner = computed<boolean>(() => {
    return authStore.isSignedInAsPartner;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Breakpoints, null, {
      "max-sm": _withCtx(() => [
        _createVNode(ProductDetailIntro, {
          name: _unref(productData).name || _unref(productData).fullName,
          designer: _unref(productData).designer
        }, null, 8, ["name", "designer"]),
        _createVNode(ProductDetailClimateEmissions)
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(Breakpoints, null, {
          "min-md": _withCtx(() => [
            _createVNode(ProductDetailIntro, {
              name: _unref(productData).name || _unref(productData).fullName,
              designer: _unref(productData).designer
            }, null, 8, ["name", "designer"]),
            _createVNode(ProductDetailClimateEmissions)
          ]),
          _: 1
        }),
        (_unref(is3DConfiguratorFeatureEnabled))
          ? (_openBlock(), _createBlock(_unref(ModuleConfigurator), {
              key: 0,
              "initial-product": _unref(productData).productId,
              "initial-variant": _unref(variantId),
              "is-toggled": _unref(isConfiguratorToggled),
              cover: _unref(coverMaterial),
              toggle: _unref(closeConfigurator),
              series: _unref(productData).familyName || ''
            }, null, 8, ["initial-product", "initial-variant", "is-toggled", "cover", "toggle", "series"]))
          : _createCommentVNode("", true),
        _createVNode(CustomizerMini),
        (_unref(productData).hasMultipleConfigurableModules && _unref(labels).showComposingModulesBtnText.length)
          ? (_openBlock(), _createBlock(_unref(ProductConfigurableModulesOverlay), {
              key: 1,
              "product-id": _unref(productData).id,
              "configuration-id": _unref(configurationId),
              class: "my-30"
            }, null, 8, ["product-id", "configuration-id"]))
          : _createCommentVNode("", true),
        _createVNode(ProductAddOns, {
          "number-of-items": _unref(productAddOnCount),
          sku: _unref(variantId)
        }, null, 8, ["number-of-items", "sku"]),
        _createVNode(ProductDetailPrice, {
          price: _unref(productData).price,
          "show-freight-information": _unref(productData).hasFreightInformation,
          "show-buy-x-and-get-y-percent": _unref(productData).hasBuyXAndGetYDiscountOption
        }, null, 8, ["price", "show-freight-information", "show-buy-x-and-get-y-percent"]),
        (_unref(isSignedInAsPartner))
          ? (_openBlock(), _createBlock(ProductDetailNetPrice, { key: 2 }))
          : _createCommentVNode("", true),
        _renderSlot(_ctx.$slots, "default"),
        (!_unref(configurationId))
          ? (_openBlock(), _createBlock(ProductDetailItemId, { key: 3 }))
          : _createCommentVNode("", true),
        (_unref(productData).showCalculateDeliveryTimeOption)
          ? (_openBlock(), _createBlock(ProductDetailDelivery, {
              key: 4,
              id: _unref(productData).id
            }, null, 8, ["id"]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(ProductDetailVisualization, {
          "product-images": _unref(productImages),
          "cover-material": _unref(coverMaterial),
          "configurator-images": _unref(configurationImages),
          "environmental-images": _unref(environmentalImages),
          alt: _unref(productData).name
        }, {
          default: _withCtx(() => [
            _createVNode(ProductDetailCertification, {
              "lighting-specification": _unref(lightingSpecification),
              "product-certification-primary": _unref(productCertificationPrimary)
            }, null, 8, ["lighting-specification", "product-certification-primary"])
          ]),
          _: 1
        }, 8, ["product-images", "cover-material", "configurator-images", "environmental-images", "alt"])
      ]),
      _createVNode(CustomizerOverlays)
    ]),
    (_unref(jsonLd))
      ? (_openBlock(), _createBlock(_resolveDynamicComponent('script'), {
          key: 0,
          type: "application/ld+json"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(jsonLd)), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(ProductDetailUpsellOverlay)
  ]))
}
}

})